import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { getApiUrl, axiosPost } from '../../helpers/helperApp';
import { LoginContext } from '../../hooks/context/LoginContext';
import useLogicForm from '../../hooks/useLogicForm';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import MiDataTableServer from '../../componentes/ui/MiDataTableServer';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import swal from 'sweetalert';
import Spinner from 'react-bootstrap/Spinner';
import useServerPagination from '../../hooks/useServerPagination';


function References({ agentName, agentId, agentType }) {
    const {
        formularioPrin,
        saveFormularioPrin,
        handleInputForm,
        resetFormPrin,
        saveLoadingTable
    } = useLogicForm();

    const [estadoForm, setEstadoForm] = useState('new');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [isSearchingArticle, setIsSearchingArticle] = useState(false);

    // Estados para la búsqueda paginada de referencias del proveedor (autocompletado en formulario)
    const [refSearchTerm, setRefSearchTerm] = useState('');
    const [supplierRefs, setSupplierRefs] = useState([]);
    const [refPage, setRefPage] = useState(1);
    const [totalRefPages, setTotalRefPages] = useState(1);
    const [isRefLoading, setIsRefLoading] = useState(false);
    const [showRefSuggestions, setShowRefSuggestions] = useState(false);

    // Estados para la búsqueda paginada de artículos (autocompletado en formulario)
    const [articleSearchTerm, setArticleSearchTerm] = useState('');
    const [articleSuggestions, setArticleSuggestions] = useState([]);
    const [articlePage, setArticlePage] = useState(1);
    const [totalArticlePages, setTotalArticlePages] = useState(1);
    const [isArticleLoading, setIsArticleLoading] = useState(false);
    const [showArticleSuggestions, setShowArticleSuggestions] = useState(false);

    const [confPantalla, setConfPantalla] = useState(null);

    const { apiKey, confApp } = useContext(LoginContext);
    const apiKeyRef = useRef(apiKey);
    const saveLoadingTableRef = useRef(saveLoadingTable);
    apiKeyRef.current = apiKey;
    saveLoadingTableRef.current = saveLoadingTable;

    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');

    const confReferencias = {
        padre: {
            modelform: {
                id: '',
                idpro: agentId || '',
                refpro: '',
                des: '',
                idart: '',
                nomart: ''
            }
        }
    };

    // Opciones del hook de paginación
    const paginationOptions = useMemo(() => ({
        apiEndpoint: '/articulos/getReferencesFromSupplier',
        apiKey: apiKeyRef.current,
        setLoadingTableExternal: saveLoadingTableRef.current,
        additionalParams: { supplierId: agentId },
        requiredParams: ['supplierId'],
        perPage: 5,
        sortColumn,
        sortDirection
    }), [agentId, sortColumn, sortDirection]);

    // Hook de paginación server-side
    const {
        data: references,
        loadingTable,
        totalPages,
        paginationInfo,
        handlePageChange,
        handleDataTableSearch,
        handlePageLengthChange,
        handleSortChange,
        handleNextPage,
        handlePreviousPage,
        getPaginationItems,
        reload
    } = useServerPagination({ ...paginationOptions, currentPage, setCurrentPage });

    // Handler para ordenación server-side
    const onSortChange = useCallback((column, direction) => {
        setSortColumn(column);
        setSortDirection(direction);
    }, []);

    // ─── Effects ────────────────────────────────────────────────────────────────

    useEffect(() => {
        saveFormularioPrin(prev => ({ ...prev, idpro: agentId || '' }));
        setEstadoForm('new');
    }, [agentId]);

    useEffect(() => {
        if (confApp) setConfPantalla(confApp);
    }, [confApp]);

    // ─── Enganchar buscador y pageLength de DataTables al hook cuando la tabla existe ── 
    useEffect(() => {
        if (!references.length) return;

        const tableSelector = '.referencias';
        const $ = window.jQuery;
        if (!$ || !$.fn.DataTable || !$.fn.DataTable.isDataTable(tableSelector)) return;

        let oTable;
        try { oTable = $(tableSelector).DataTable(); } catch (e) { return; }
        if (!oTable) return;

        // Búsqueda backend
        let searchDebounce = null;
        const handleSearch = (e, settings) => {
            if (searchDebounce) clearTimeout(searchDebounce);
            searchDebounce = setTimeout(() => {
                const val = settings.oPreviousSearch?.sSearch ?? '';
                handleDataTableSearch(val);
            }, 300);
        };

        // Cambio de pageLength backend
        const handleLength = (e, settings, len) => {
            handlePageLengthChange(len);
        };

        oTable.off('search.dt.refbackend').on('search.dt.refbackend', handleSearch);
        oTable.off('length.dt.refbackend').on('length.dt.refbackend', handleLength);

        return () => {
            try {
                oTable.off('search.dt.refbackend');
                oTable.off('length.dt.refbackend');
            } catch (e) {}
        };
    }, [references, handleDataTableSearch, handlePageLengthChange]);
    // ────────────────────────────────────────────────────────────────────────────────

    // Debounce para autocompletado de referencias del proveedor en el formulario
    useEffect(() => {
        if (!agentId) return;
        if (!refSearchTerm.trim()) {
            setSupplierRefs([]);
            return;
        }
        const handler = setTimeout(() => {
            setSupplierRefs([]);
            fetchReferencesForAgent(agentId, 1, refSearchTerm);
        }, 350);
        return () => clearTimeout(handler);
    }, [refSearchTerm, agentId]);

    // Debounce para autocompletado de artículos en el formulario
    useEffect(() => {
        if (!isSearchingArticle && !formularioPrin.idart) return;
        const handler = setTimeout(() => {
            if (articleSearchTerm) {
                fetchArticles(articleSearchTerm, 1);
            } else {
                setArticleSuggestions([]);
            }
        }, 300);
        return () => clearTimeout(handler);
    }, [articleSearchTerm]);


    // ─── API calls ───────────────────────────────────────────────────────────────

    // Autocompletado de referencias del proveedor en el formulario
    const fetchReferencesForAgent = async (supplierId, page, search = '') => {
        if (!supplierId || (isRefLoading && page === 1)) return;
        setIsRefLoading(true);
        try {
            const data = new FormData();
            data.append('validacion', apiKey());
            data.append('supplierId', supplierId);
            data.append('page', page);
            data.append('perPage', 5);
            if (search) data.append('searchTerm', search);

            const url = `${getApiUrl(window.location.origin)}/articulos/getReferencesFromSupplier`;
            const response = await axiosPost(url, data);

            if (response.data.success) {
                const newRefs = (response.data.data || []).map(item => ({
                    id: item.id,
                    cod: item.cod,
                    nom: item.nom || ''
                }));
                setSupplierRefs(prev => page === 1 ? newRefs : [...prev, ...newRefs]);
                setTotalRefPages(response.data.pagination.totalPages);
                setRefPage(page);
            } else if (page === 1) {
                setSupplierRefs([]);
            }
        } catch (error) {
            console.error('Error al cargar las referencias del agente:', error);
            if (page === 1) setSupplierRefs([]);
        } finally {
            setIsRefLoading(false);
        }
    };

    // Autocompletado de artículos en el formulario
    const fetchArticles = async (search, page) => {
        setIsArticleLoading(true);
        try {
            const data = new FormData();
            data.append('validacion', apiKey());
            data.append('searchTerm', search);
            data.append('page', page);
            data.append('perPage', 25);

            const url = `${getApiUrl(window.location.origin)}/articulos/getArticlesToSupplier`;
            const response = await axiosPost(url, data);

            if (response.data.success) {
                const newArticles = response.data.data || [];
                setArticleSuggestions(prev => page === 1 ? newArticles : [...prev, ...newArticles]);
                setTotalArticlePages(response.data.pagination.totalPages);
                setArticlePage(page);
            }
        } catch (error) {
            console.error('Error al buscar artículos:', error);
        } finally {
            setIsArticleLoading(false);
        }
    };


    // ─── Handlers de tabla ───────────────────────────────────────────────────────

    const handleEdit = (e) => {
        const id = typeof e === 'object' ? e.target.id : e;
        const record = references.find(r => parseInt(r.id) === parseInt(id));
        if (record) {
            saveFormularioPrin({
                id: record.id,
                idpro: record.idage,        // idage = id del agente/proveedor
                refpro: record.cod,         // cod = referencia del proveedor
                des: record.nom,            // nom = descripción
                idart: record.iduni,        // iduni = id del artículo
                nomart: record.nom_articulo || ''
            });
            setArticleSearchTerm(record.nom_articulo || '');
            setEstadoForm('edit');
            setIsSearchingArticle(false);
            setRefSearchTerm(record.cod || '');
            setShowCreateForm(false);
        }
    };

    const handleDelete = async (e) => {
        const willDelete = await swal({
            title: '¿Estás seguro?',
            text: 'Una vez eliminada, no podrás recuperar esta referencia.',
            icon: 'warning',
            buttons: ['Cancelar', 'Eliminar'],
            dangerMode: true,
        });

        if (willDelete) {
            try {
                const id = typeof e === 'object' ? e.target.id : e;
                const data = new FormData();
                data.append('validacion', apiKey());
                data.append('id', id);

                const url = `${getApiUrl(window.location.origin)}/agentes/referencias/delete`;
                await axiosPost(url, data);

                reload();
                swal('Eliminado', 'La referencia ha sido eliminada con éxito.', 'success');
            } catch (error) {
                console.error('Error al eliminar la referencia:', error);
                swal('Error', 'Ocurrió un problema al conectar con el servidor.', 'error');
            }
        }
    };


    // ─── Handlers de formulario ──────────────────────────────────────────────────

    const handleCancelEdit = () => {
        resetFormPrin(confReferencias.padre.modelform);
        saveFormularioPrin(prev => ({ ...prev, idpro: agentId || '' }));
        setArticleSearchTerm('');
        setEstadoForm('new');
        setIsSearchingArticle(false);
        setRefSearchTerm('');
        setSupplierRefs([]);
        setShowRefSuggestions(false);
        setShowCreateForm(false);
    };

    const validateForm = (newRefPro, isEditing) => {
        if (!newRefPro) {
            swal('Error de validación', "El campo 'Referencia' no puede estar vacío.", 'error');
            return false;
        }

        // Usar los campos reales del backend: cod = referencia, iduni = artículo
        const duplicate = references.find(ref => {
            const sameCode = ref.cod.trim().toLowerCase() === newRefPro.toLowerCase();
            return isEditing
                ? ref.id !== formularioPrin.id && sameCode
                : sameCode;
        });

        if (duplicate) {
            const msg = duplicate.iduni === formularioPrin.idart
                ? 'La combinación de esta referencia y artículo ya existe para el proveedor.'
                : 'El código de referencia ya está en uso para otro artículo de este proveedor.';
            swal('Error de validación', msg, 'error');
            return false;
        }

        return true;
    };

    const handleSave = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        const newRefPro = formularioPrin.refpro ? formularioPrin.refpro.trim() : '';
        const isEditing = !!formularioPrin.id;

        if (!validateForm(newRefPro, isEditing)) return;

        try {
            if (showCreateForm && !isEditing) {
                // ── CREAR ──
                const payload = {
                    iduni: formularioPrin.idart,
                    idage: formularioPrin.idpro,
                    cod: newRefPro,
                    nom: formularioPrin.des || ''
                };

                const data = new FormData();
                data.append('validacion', apiKey());
                data.append('payload', JSON.stringify(payload));

                const url = `${getApiUrl(window.location.origin)}/agentes/createArticleReference`;
                const response = await axiosPost(url, data);

                if (response.data && response.data.success !== false) {
                    swal('¡Guardado!', 'La referencia ha sido creada con éxito.', 'success');
                    reload();
                    handleCancelEdit();
                } else {
                    swal('Error', response.data.message || 'No se pudo crear la referencia.', 'error');
                }

            } else if (isEditing) {
                // ── EDITAR ──
                const payload = {
                    id: formularioPrin.id,
                    iduni: formularioPrin.idart,
                    idage: formularioPrin.idpro,
                    cod: newRefPro,
                    nom: formularioPrin.des
                };

                const data = new FormData();
                data.append('validacion', apiKey());
                data.append('payload', JSON.stringify(payload));

                const url = `${getApiUrl(window.location.origin)}/agentes/updateArticleReference`;
                const response = await axiosPost(url, data);

                if (response.data && response.data.success !== false) {
                    swal('¡Actualizado!', 'La referencia ha sido guardada con éxito.', 'success');
                    reload();
                    handleCancelEdit();
                } else {
                    swal('Error', response.data.message || 'El servidor indicó un problema al guardar la referencia.', 'error');
                }
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'No se pudo conectar con el servidor.';
            swal('Error', msg, 'error');
        }
    };

    const handleArticleSearchChange = (e) => {
        setArticleSearchTerm(e.target.value);
        setShowArticleSuggestions(true);
    };

    const handleSelectArticle = (article) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = article.unidad;
        const articleName = tempDiv.textContent || tempDiv.innerText || '';

        setArticleSearchTerm(articleName);
        saveFormularioPrin(prev => ({ ...prev, idart: article.id, nomart: article.unidad }));
        setShowArticleSuggestions(false);
    };

    const handleRefSearchChange = (e) => {
        const value = e.target.value;
        setRefSearchTerm(value);
        saveFormularioPrin(prev => ({ ...prev, refpro: value }));
        if (value.trim()) {
            setShowRefSuggestions(true);
        } else {
            setShowRefSuggestions(false);
            setSupplierRefs([]);
        }
    };

    const handleSelectRef = (ref) => {
        setRefSearchTerm(ref.cod);
        saveFormularioPrin(prev => ({ ...prev, refpro: ref.cod, des: ref.nom }));
        setShowRefSuggestions(false);
    };


    // ─── Columnas de tabla ───────────────────────────────────────────────────────

    // Columnas mapeadas a los campos reales del backend:
    // cod → referencia proveedor, nom → descripción, iduni → artículo asociado
    const columns = [
        { title: 'Referencia Proveedor', data: 'cod' },
        { title: 'Descripción', data: 'nom' },
        {
            title: 'Artículo Asociado',
            data: 'nom_articulo',   // campo enriquecido si el backend lo devuelve, o null
            defaultContent: '<span class="text-muted fst-italic">No asociado</span>',
            render: (data, type, row) => {
                if (type === 'display') {
                    return row.nom_articulo
                        ? row.nom_articulo
                        : '<span class="text-muted fst-italic">No asociado</span>';
                }
                return row.nom_articulo || '';
            }
        }
    ];

    const paginationItems = getPaginationItems();


    // ─── Render ──────────────────────────────────────────────────────────────────

    return (
        <Container fluid className="mt-4">
            <h2 className="mb-4">Gestión de Referencias - {agentName}</h2>

            {agentId && (
                <div className="mb-3">
                    <Button
                        variant="primary"
                        onClick={() => {
                            setShowCreateForm(true);
                            setEstadoForm('new');
                            resetFormPrin(confReferencias.padre.modelform);
                            saveFormularioPrin(prev => ({ ...prev, idpro: agentId || '' }));
                            setArticleSearchTerm('');
                            setRefSearchTerm('');
                            setSupplierRefs([]);
                            setShowRefSuggestions(false);
                            setIsSearchingArticle(true);
                        }}
                    >
                        <i className="fas fa-plus me-1"></i> Nueva Referencia
                    </Button>
                </div>
            )}

            {agentId && (
                <>
                    {/* Mensaje de vacío — la tabla siempre está montada para que DataTables
                         no se desmonte/remonte al llegar los primeros datos */}
                    {!loadingTable && references.length === 0 && (
                        <div className="alert alert-info">
                            No hay referencias registradas para este agente.
                        </div>
                    )}

                    <MiDataTableServer
                        id="referencias"
                        columns={columns}
                        data={references}
                        columnas={columns}
                        columnactions="Acciones"
                        handleEditReg={handleEdit}
                        handleDeleteItem={handleDelete}
                        formulario={{ id: '' }}
                        linkColumns={[]}
                        loadingTable={loadingTable}
                        traduction={{
                            sprocessing: { title: 'Procesando...' },
                            slengthmenu: { title: 'Mostrar _MENU_ registros' },
                            szerorecords: { title: 'No se encontraron resultados' },
                            semptytable: { title: 'Ningún dato disponible en esta tabla' },
                            sinfo: { title: 'Mostrando registros del _START_ al _END_ de un total de _TOTAL_ registros' },
                            sinfoempty: { title: 'Mostrando registros del 0 al 0 de un total de 0 registros' },
                            sinfofiltered: { title: '(filtrado de un total de _MAX_ registros)' },
                            sinfopostfix: { title: '' },
                            ssearch: { title: 'Buscar:' },
                            surl: { title: '' },
                            sinfothousands: { title: ',' },
                            sloadingrecords: { title: 'Cargando...' },
                            sfirst: { title: 'Primero' },
                            slast: { title: 'Último' },
                            snext: { title: 'Siguiente' },
                            sprevious: { title: 'Anterior' },
                            ssortascending: { title: ': Activar para ordenar la columna de manera ascendente' },
                            ssortdescending: { title: ': Activar para ordenar la columna de manera descendente' },
                            rows: { title: '%d filas seleccionadas' },
                            copytitle: { title: 'Copiar al portapapeles' },
                            copykeys: { title: 'Use Ctrf+C para copiar los datos de la tabla a su portapapeles.' },
                            copysuccess: {
                                many: { title: 'Copiadas %d filas al portapapeles' },
                                one: { title: 'Copiada 1 fila al portapapeles' }
                            },
                        }}
                        paging={false}
                        searching={true}
                        ordering={true}
                        pagelength="5"
                        searchBackend={true}
                        onSearchChange={handleDataTableSearch}
                        onSortChange={onSortChange}
                        onPageLengthChange={handlePageLengthChange}
                        showNativePaging={false}
                        showNativeInfo={false}
                        showNativeSearch={true}
                        showNativeLengthMenu={false}
                    />

                    {/* Paginación */}
                    <div className="row">
                        <div className="col-sm-12 col-md-5">
                            <div className="dataTables_info" role="status" aria-live="polite">
                                {paginationInfo}
                            </div>
                        </div>
                        <div className="col-sm-12 col-md-7">
                            <div className="dataTables_paginate paging_simple_numbers">
                                <ul className="pagination justify-content-end">
                                    <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <a href="#" className="page-link" onClick={handlePreviousPage}>
                                            {confPantalla?.config?.trad?.components?.datatable?.spaginate?.sPrevious?.title || 'Anterior'}
                                        </a>
                                    </li>
                                    {paginationItems.map((item, index) => (
                                        <li
                                            key={index}
                                            className={`paginate_button page-item ${item === currentPage ? 'active' : ''} ${item === '...' ? 'disabled' : ''}`}
                                        >
                                            <a
                                                href="#"
                                                className="page-link"
                                                onClick={() => item !== '...' && handlePageChange(item)}
                                            >
                                                {item}
                                            </a>
                                        </li>
                                    ))}
                                    <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <a href="#" className="page-link" onClick={handleNextPage}>
                                            {confPantalla?.config?.trad?.components?.datatable?.spaginate?.sNext?.title || 'Siguiente'}
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Formulario de Edición / Creación */}
                    {(estadoForm === 'edit' || showCreateForm) && (
                        <div className="mt-4 p-3 border rounded bg-light">
                            <h5 className="mb-3">{showCreateForm ? 'Nueva Referencia' : 'Editar Referencia'}</h5>
                            <>
                                <Row className="mb-3">
                                    {/* Campo: Referencia proveedor con autocompletado */}
                                    <Col md={3} className="position-relative">
                                        <Form.Group>
                                            <Form.Label>Referencia</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="refpro"
                                                placeholder="Escribe una referencia"
                                                value={refSearchTerm}
                                                onChange={handleRefSearchChange}
                                                onFocus={() => refSearchTerm.trim() && setShowRefSuggestions(true)}
                                                onBlur={() => setTimeout(() => setShowRefSuggestions(false), 200)}
                                                autoComplete="off"
                                                required
                                            />
                                            {showRefSuggestions && refSearchTerm.trim() && (
                                                <ListGroup style={{ position: 'absolute', zIndex: 1000, width: '100%' }}>
                                                    {isRefLoading && refPage === 1 ? (
                                                        <ListGroup.Item className="text-center">
                                                            <Spinner animation="border" size="sm" /> Cargando...
                                                        </ListGroup.Item>
                                                    ) : supplierRefs.length > 0 ? (
                                                        <>
                                                            {supplierRefs.map(ref => (
                                                                <ListGroup.Item key={ref.id} action onClick={() => handleSelectRef(ref)}>
                                                                    {ref.cod}
                                                                </ListGroup.Item>
                                                            ))}
                                                            {refPage < totalRefPages && (
                                                                <ListGroup.Item
                                                                    action
                                                                    onClick={() => fetchReferencesForAgent(agentId, refPage + 1, refSearchTerm)}
                                                                    className="text-center"
                                                                >
                                                                    {isRefLoading ? <Spinner animation="border" size="sm" /> : 'Cargar más'}
                                                                </ListGroup.Item>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <ListGroup.Item disabled>No se encontraron referencias</ListGroup.Item>
                                                    )}
                                                </ListGroup>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    {/* Campo: Descripción */}
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Descripción</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="des"
                                                placeholder="Escribe una descripción"
                                                value={formularioPrin.des || ''}
                                                onChange={(e) => handleInputForm(e, formularioPrin, saveFormularioPrin)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Campo: Artículo asociado con autocompletado */}
                                    <Col md={5} className="position-relative">
                                        <Form.Group>
                                            <Form.Label>Artículo Asociado</Form.Label>
                                            {isSearchingArticle || !formularioPrin.idart ? (
                                                <>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Escribe para buscar un artículo..."
                                                        value={articleSearchTerm}
                                                        onChange={handleArticleSearchChange}
                                                        autoComplete="off"
                                                        autoFocus
                                                    />
                                                    {showArticleSuggestions && articleSearchTerm && (
                                                        <ListGroup
                                                            className="position-absolute w-100"
                                                            style={{ zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}
                                                        >
                                                            {articleSuggestions.map(article => (
                                                                <ListGroup.Item
                                                                    key={article.id}
                                                                    action
                                                                    onClick={() => handleSelectArticle(article)}
                                                                    dangerouslySetInnerHTML={{ __html: article.unidad }}
                                                                />
                                                            ))}
                                                            {isArticleLoading && (
                                                                <ListGroup.Item className="text-center">
                                                                    <Spinner animation="border" size="sm" />
                                                                </ListGroup.Item>
                                                            )}
                                                            {!isArticleLoading && articlePage < totalArticlePages && (
                                                                <ListGroup.Item
                                                                    action
                                                                    onClick={() => fetchArticles(articleSearchTerm, articlePage + 1)}
                                                                    className="text-center text-primary"
                                                                >
                                                                    Cargar más resultados...
                                                                </ListGroup.Item>
                                                            )}
                                                        </ListGroup>
                                                    )}
                                                </>
                                            ) : (
                                                <InputGroup>
                                                    <Form.Control value={articleSearchTerm} readOnly />
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => {
                                                            setIsSearchingArticle(true);
                                                            setArticleSearchTerm('');
                                                            saveFormularioPrin(prev => ({ ...prev, idart: null, nomart: '' }));
                                                        }}
                                                    >
                                                        Cambiar
                                                    </Button>
                                                </InputGroup>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Button variant="success" type="button" onClick={handleSave} className="me-2">
                                    <i className="fas fa-save me-1"></i> Guardar
                                </Button>
                                <Button variant="secondary" onClick={handleCancelEdit}>
                                    Cancelar
                                </Button>
                            </>
                        </div>
                    )}
                </>
            )}
        </Container>
    );
}

export default References;